import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as Struct from "effect/Struct";
import { ExecutionEnvironmentDescriptor } from "./environment.js";
import { ServerAuthDescriptor } from "./authorization.js";
import { PolicyCapabilities } from "./actionPolicy.js";
import { OrganizationQuotas } from "./quotas.js";
import { IsoDateTime, NonNegativeInt, PositiveInt, ProjectId, ThreadId, TrimmedNonEmptyString, } from "./baseSchemas.js";
import { KeybindingCommand, KeybindingValue, KeybindingWhen, ResolvedKeybindingsConfig, } from "./keybindings.js";
import { PlanFeatures, PlanSettings, SettingsSource } from "./planSettings.js";
import { ModelCapabilities } from "./model.js";
import { ProviderDriverKind, ProviderInstanceId } from "./providerInstance.js";
const KeybindingsMalformedConfigIssue = Schema.Struct({
    kind: Schema.Literal("keybindings.malformed-config"),
    message: TrimmedNonEmptyString,
});
const KeybindingsInvalidEntryIssue = Schema.Struct({
    kind: Schema.Literal("keybindings.invalid-entry"),
    message: TrimmedNonEmptyString,
    index: Schema.Number,
});
export const ServerConfigIssue = Schema.Union([
    KeybindingsMalformedConfigIssue,
    KeybindingsInvalidEntryIssue,
]);
const ServerConfigIssues = Schema.Array(ServerConfigIssue);
export const ServerProviderState = Schema.Literals(["ready", "warning", "error", "disabled"]);
export const ServerProviderAuthStatus = Schema.Literals([
    "authenticated",
    "unauthenticated",
    "unknown",
]);
export const ServerProviderAuth = Schema.Struct({
    status: ServerProviderAuthStatus,
    type: Schema.optional(TrimmedNonEmptyString),
    label: Schema.optional(TrimmedNonEmptyString),
    email: Schema.optional(TrimmedNonEmptyString),
});
export const ServerProviderModel = Schema.Struct({
    slug: TrimmedNonEmptyString,
    name: TrimmedNonEmptyString,
    shortName: Schema.optional(TrimmedNonEmptyString),
    subProvider: Schema.optional(TrimmedNonEmptyString),
    isCustom: Schema.Boolean,
    capabilities: Schema.NullOr(ModelCapabilities),
});
export const ServerProviderSlashCommandInput = Schema.Struct({
    hint: TrimmedNonEmptyString,
});
export const ServerProviderSlashCommand = Schema.Struct({
    name: TrimmedNonEmptyString,
    description: Schema.optional(TrimmedNonEmptyString),
    input: Schema.optional(ServerProviderSlashCommandInput),
});
export const ServerProviderSkill = Schema.Struct({
    name: TrimmedNonEmptyString,
    description: Schema.optional(TrimmedNonEmptyString),
    path: TrimmedNonEmptyString,
    scope: Schema.optional(TrimmedNonEmptyString),
    enabled: Schema.Boolean,
    displayName: Schema.optional(TrimmedNonEmptyString),
    shortDescription: Schema.optional(TrimmedNonEmptyString),
});
/**
 * Availability of a configured provider instance from the runtime's POV.
 *
 *  - `available` — the build ships this driver and an instance is wired
 *    up. Default for legacy snapshots produced from the closed
 *    `ServerSettings.providers` map.
 *  - `unavailable` — the user's `ServerSettings.providerInstances` (or a
 *    persisted thread / session binding) references a driver this build
 *    doesn't ship. Common after rolling back from a fork or PR branch
 *    that introduced a new driver. The snapshot is preserved so the UI
 *    can render "missing driver" affordances and so the data round-trips
 *    when the user moves back to the fork.
 *
 * Snapshots with `availability: "unavailable"` MUST set
 * `installed: false` and `enabled: false`; the runtime refuses turn
 * starts against them with a structured error.
 */
export const ServerProviderAvailability = Schema.Literals(["available", "unavailable"]);
export const ServerProviderContinuation = Schema.Struct({
    groupKey: TrimmedNonEmptyString,
});
export const ServerProvider = Schema.Struct({
    // Routing key for the configured instance this snapshot represents. This
    // is the only stable identity consumers may use for provider routing.
    instanceId: ProviderInstanceId,
    // Open driver kind slug that selects the implementation handling this
    // instance. It is metadata/capability context, not a routing key.
    driver: ProviderDriverKind,
    displayName: Schema.optional(TrimmedNonEmptyString),
    accentColor: Schema.optional(TrimmedNonEmptyString),
    badgeLabel: Schema.optional(TrimmedNonEmptyString),
    continuation: Schema.optional(ServerProviderContinuation),
    requiresNewThreadForModelChange: Schema.optional(Schema.Boolean),
    enabled: Schema.Boolean,
    installed: Schema.Boolean,
    version: Schema.NullOr(TrimmedNonEmptyString),
    status: ServerProviderState,
    auth: ServerProviderAuth,
    checkedAt: IsoDateTime,
    message: Schema.optional(TrimmedNonEmptyString),
    // Optional for back-compat: every legacy producer omits this field and
    // an absent value is interpreted as `"available"` by consumers (see
    // `isProviderAvailable`). New `ProviderInstanceRegistry` outputs set it
    // explicitly so the UI can render unavailable shadows from
    // `ServerSettings.providerInstances`.
    availability: Schema.optional(ServerProviderAvailability),
    // Human-readable reason populated when `availability === "unavailable"`.
    // Surfaces in the UI alongside the missing-driver affordance.
    unavailableReason: Schema.optional(TrimmedNonEmptyString),
    models: Schema.Array(ServerProviderModel),
    slashCommands: Schema.Array(ServerProviderSlashCommand).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
    skills: Schema.Array(ServerProviderSkill).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
/** Status of the provider instance the agent runs on, as clients receive it. */
export const ServerAgentStatus = ServerProvider.mapFields((fields) => ({
    ...Struct.pick(fields, ["status", "slashCommands", "skills"]),
    auth: ServerProviderAuth.mapFields(Struct.pick(["status"])),
}));
/**
 * Treat the optional `availability` as "available" when absent. This is
 * the rule legacy producers (which omit the field) and new producers
 * (which set it explicitly) agree on so consumers never have to thread
 * `?? "available"` defaults through their code paths.
 */
export const isProviderAvailable = (snapshot) => snapshot.availability !== "unavailable";
export const ServerObservability = Schema.Struct({
    logsDirectoryPath: TrimmedNonEmptyString,
    localTracingEnabled: Schema.Boolean,
    otlpTracesUrl: Schema.optional(TrimmedNonEmptyString),
    otlpTracesEnabled: Schema.Boolean,
    otlpMetricsUrl: Schema.optional(TrimmedNonEmptyString),
    otlpMetricsEnabled: Schema.Boolean,
});
export const ServerTraceDiagnosticsErrorKind = Schema.Literals([
    "trace-file-not-found",
    "trace-file-read-failed",
]);
export const ServerTraceDiagnosticsSpanSummary = Schema.Struct({
    name: TrimmedNonEmptyString,
    count: NonNegativeInt,
    failureCount: NonNegativeInt,
    totalDurationMs: Schema.Number,
    averageDurationMs: Schema.Number,
    maxDurationMs: Schema.Number,
});
export const ServerTraceDiagnosticsFailureSummary = Schema.Struct({
    name: TrimmedNonEmptyString,
    cause: TrimmedNonEmptyString,
    count: NonNegativeInt,
    lastSeenAt: Schema.DateTimeUtc,
    traceId: TrimmedNonEmptyString,
    spanId: TrimmedNonEmptyString,
});
export const ServerTraceDiagnosticsRecentFailure = Schema.Struct({
    name: TrimmedNonEmptyString,
    cause: TrimmedNonEmptyString,
    durationMs: Schema.Number,
    endedAt: Schema.DateTimeUtc,
    traceId: TrimmedNonEmptyString,
    spanId: TrimmedNonEmptyString,
});
export const ServerTraceDiagnosticsSpanOccurrence = Schema.Struct({
    name: TrimmedNonEmptyString,
    durationMs: Schema.Number,
    endedAt: Schema.DateTimeUtc,
    traceId: TrimmedNonEmptyString,
    spanId: TrimmedNonEmptyString,
});
export const ServerTraceDiagnosticsLogEvent = Schema.Struct({
    spanName: TrimmedNonEmptyString,
    level: TrimmedNonEmptyString,
    message: TrimmedNonEmptyString,
    seenAt: Schema.DateTimeUtc,
    traceId: TrimmedNonEmptyString,
    spanId: TrimmedNonEmptyString,
});
export const ServerTraceDiagnosticsResult = Schema.Struct({
    traceFilePath: TrimmedNonEmptyString,
    scannedFilePaths: Schema.Array(TrimmedNonEmptyString),
    readAt: Schema.DateTimeUtc,
    recordCount: NonNegativeInt,
    parseErrorCount: NonNegativeInt,
    firstSpanAt: Schema.Option(Schema.DateTimeUtc),
    lastSpanAt: Schema.Option(Schema.DateTimeUtc),
    failureCount: NonNegativeInt,
    interruptionCount: NonNegativeInt,
    slowSpanThresholdMs: NonNegativeInt,
    slowSpanCount: NonNegativeInt,
    logLevelCounts: Schema.Record(TrimmedNonEmptyString, NonNegativeInt),
    topSpansByCount: Schema.Array(ServerTraceDiagnosticsSpanSummary),
    slowestSpans: Schema.Array(ServerTraceDiagnosticsSpanOccurrence),
    commonFailures: Schema.Array(ServerTraceDiagnosticsFailureSummary),
    latestFailures: Schema.Array(ServerTraceDiagnosticsRecentFailure),
    latestWarningAndErrorLogs: Schema.Array(ServerTraceDiagnosticsLogEvent),
    partialFailure: Schema.Option(Schema.Boolean),
    error: Schema.Option(Schema.Struct({
        kind: ServerTraceDiagnosticsErrorKind,
        message: TrimmedNonEmptyString,
    })),
});
export const ServerProcessSignal = Schema.Literals(["SIGINT", "SIGKILL"]);
export const ServerProcessDiagnosticsEntry = Schema.Struct({
    pid: PositiveInt,
    ppid: NonNegativeInt,
    pgid: Schema.Option(Schema.Int),
    status: TrimmedNonEmptyString,
    cpuPercent: Schema.Number,
    rssBytes: NonNegativeInt,
    elapsed: TrimmedNonEmptyString,
    command: TrimmedNonEmptyString,
    depth: NonNegativeInt,
    childPids: Schema.Array(PositiveInt),
});
export const ServerProcessDiagnosticsResult = Schema.Struct({
    serverPid: PositiveInt,
    readAt: Schema.DateTimeUtc,
    processCount: NonNegativeInt,
    totalRssBytes: NonNegativeInt,
    totalCpuPercent: Schema.Number,
    processes: Schema.Array(ServerProcessDiagnosticsEntry),
    error: Schema.Option(Schema.Struct({
        message: TrimmedNonEmptyString,
    })),
});
export const ServerProcessResourceHistoryInput = Schema.Struct({
    windowMs: NonNegativeInt,
    bucketMs: NonNegativeInt,
});
export const ServerProcessResourceHistoryBucket = Schema.Struct({
    startedAt: Schema.DateTimeUtc,
    endedAt: Schema.DateTimeUtc,
    avgCpuPercent: Schema.Number,
    maxCpuPercent: Schema.Number,
    maxRssBytes: NonNegativeInt,
    maxProcessCount: NonNegativeInt,
});
export const ServerProcessResourceHistorySummary = Schema.Struct({
    processKey: TrimmedNonEmptyString,
    pid: PositiveInt,
    ppid: NonNegativeInt,
    command: TrimmedNonEmptyString,
    depth: NonNegativeInt,
    isServerRoot: Schema.Boolean,
    firstSeenAt: Schema.DateTimeUtc,
    lastSeenAt: Schema.DateTimeUtc,
    currentCpuPercent: Schema.Number,
    avgCpuPercent: Schema.Number,
    maxCpuPercent: Schema.Number,
    cpuSecondsApprox: Schema.Number,
    currentRssBytes: NonNegativeInt,
    maxRssBytes: NonNegativeInt,
    sampleCount: NonNegativeInt,
});
export const ServerProcessResourceHistoryResult = Schema.Struct({
    readAt: Schema.DateTimeUtc,
    windowMs: NonNegativeInt,
    bucketMs: NonNegativeInt,
    sampleIntervalMs: NonNegativeInt,
    retainedSampleCount: NonNegativeInt,
    totalCpuSecondsApprox: Schema.Number,
    buckets: Schema.Array(ServerProcessResourceHistoryBucket),
    topProcesses: Schema.Array(ServerProcessResourceHistorySummary),
    error: Schema.Option(Schema.Struct({
        message: TrimmedNonEmptyString,
    })),
});
export const ServerSignalProcessInput = Schema.Struct({
    pid: PositiveInt,
    signal: ServerProcessSignal,
});
export const ServerSignalProcessResult = Schema.Struct({
    pid: PositiveInt,
    signal: ServerProcessSignal,
    signaled: Schema.Boolean,
    message: Schema.Option(TrimmedNonEmptyString),
});
/**
 * Values the operator ships with the product, as `operator-defaults.json`
 * holds them: the directory new projects are created under, and a full set of
 * {@link PlanSettings}. Always present, because a first launch has no other
 * source for any of them. The organisation's plan, as the gateway answers it,
 * is laid over the settings; `addProjectBaseDirectory` comes from the file
 * alone. Server-side only — clients receive {@link ClientOperatorDefaults}.
 */
export const OperatorDefaultsConfig = Schema.Struct({
    addProjectBaseDirectory: TrimmedNonEmptyString,
    ...PlanSettings.fields,
});
/**
 * The part of the operator defaults a client may see. The model stays with the
 * server: a client that never holds the model identity cannot show it, and has
 * nothing to send back.
 */
export const ClientOperatorDefaults = OperatorDefaultsConfig.mapFields(Struct.pick(["addProjectBaseDirectory"]));
export const ServerConfig = Schema.Struct({
    environment: ExecutionEnvironmentDescriptor,
    auth: ServerAuthDescriptor,
    cwd: TrimmedNonEmptyString,
    keybindings: ResolvedKeybindingsConfig,
    issues: ServerConfigIssues,
    agent: Schema.NullOr(ServerAgentStatus),
    observability: ServerObservability,
    features: PlanFeatures,
    quotas: Schema.NullOr(OrganizationQuotas),
    capabilities: PolicyCapabilities,
    settingsSource: SettingsSource,
    operatorDefaults: ClientOperatorDefaults,
});
const ServerUpsertKeybindingReplaceTarget = Schema.Struct({
    key: KeybindingValue,
    command: KeybindingCommand,
    when: Schema.optional(KeybindingWhen),
});
export const ServerUpsertKeybindingInput = Schema.Struct({
    key: KeybindingValue,
    command: KeybindingCommand,
    when: Schema.optional(KeybindingWhen),
    replace: Schema.optional(ServerUpsertKeybindingReplaceTarget),
});
export const ServerRemoveKeybindingInput = ServerUpsertKeybindingReplaceTarget;
export const ServerUpsertKeybindingResult = Schema.Struct({
    keybindings: ResolvedKeybindingsConfig,
    issues: ServerConfigIssues,
});
export const ServerRemoveKeybindingResult = ServerUpsertKeybindingResult;
export const ServerConfigUpdatedPayload = Schema.Struct({
    issues: ServerConfigIssues,
    agent: Schema.NullOr(ServerAgentStatus),
});
export const ServerConfigKeybindingsUpdatedPayload = Schema.Struct({
    keybindings: ResolvedKeybindingsConfig,
    issues: ServerConfigIssues,
});
export const ServerConfigAgentStatusPayload = Schema.Struct({
    agent: Schema.NullOr(ServerAgentStatus),
});
export const ServerConfigStreamSnapshotEvent = Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("snapshot"),
    config: ServerConfig,
});
export const ServerConfigStreamKeybindingsUpdatedEvent = Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("keybindingsUpdated"),
    payload: ServerConfigKeybindingsUpdatedPayload,
});
export const ServerConfigStreamAgentStatusEvent = Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("agentStatus"),
    payload: ServerConfigAgentStatusPayload,
});
export const ServerConfigStreamEvent = Schema.Union([
    ServerConfigStreamSnapshotEvent,
    ServerConfigStreamKeybindingsUpdatedEvent,
    ServerConfigStreamAgentStatusEvent,
]);
export const ServerLifecycleReadyPayload = Schema.Struct({
    at: IsoDateTime,
    environment: ExecutionEnvironmentDescriptor,
});
export const ServerLifecycleWelcomePayload = Schema.Struct({
    environment: ExecutionEnvironmentDescriptor,
    cwd: TrimmedNonEmptyString,
    projectName: TrimmedNonEmptyString,
    bootstrapProjectId: Schema.optional(ProjectId),
    bootstrapThreadId: Schema.optional(ThreadId),
});
export const ServerLifecycleStreamWelcomeEvent = Schema.Struct({
    version: Schema.Literal(1),
    sequence: NonNegativeInt,
    type: Schema.Literal("welcome"),
    payload: ServerLifecycleWelcomePayload,
});
export const ServerLifecycleStreamReadyEvent = Schema.Struct({
    version: Schema.Literal(1),
    sequence: NonNegativeInt,
    type: Schema.Literal("ready"),
    payload: ServerLifecycleReadyPayload,
});
export const ServerLifecycleStreamEvent = Schema.Union([
    ServerLifecycleStreamWelcomeEvent,
    ServerLifecycleStreamReadyEvent,
]);
